import { registerModal } from '../components/ideo/ui-modal/stack';

export type DialogController = {
    readonly dialog: HTMLDialogElement | null;
    open: (guard?: (() => boolean) | Event) => Promise<void>;
    close: () => void;
};

export type DialogTemplateRef = Ref<DialogController | null> & DialogController;

const pendingDialogOpens = new WeakMap<HTMLDialogElement, object>();

type DialogTarget = HTMLDialogElement | null | undefined;
type DialogSource = string | DialogTarget | { value: DialogTarget };

export function useDialog(): DialogTemplateRef;
export function useDialog(source: DialogSource, model?: { value: boolean }): DialogController;
export function useDialog(source?: DialogSource, model?: { value: boolean })
{
    if (source === undefined)
    {
        const dialogRef = ref<DialogController | null>(null) as DialogTemplateRef;

        Object.defineProperties(dialogRef, {
            dialog: {
                get()
                {
                    return dialogRef.value?.dialog ?? null;
                }
            },
            open: {
                value: async (guard?: (() => boolean) | Event) =>
                {
                    await dialogRef.value?.open(guard);
                }
            },
            close: {
                value: () =>
                {
                    dialogRef.value?.close();
                }
            }
        });

        return dialogRef;
    }

    return createDialogController(source, model);
}

function createDialogController(source: DialogSource, model?: { value: boolean })
{
    let dialog: HTMLDialogElement | null = null;
    let boundDialog: HTMLDialogElement | null = null;

    function resolveSource()
    {
        if (typeof source === 'string')
        {
            return source;
        }

        if (source && typeof source === 'object' && 'value' in source)
        {
            return source.value;
        }

        return source;
    }

    function resolveDialog()
    {
        if (!import.meta.client)
        {
            return null;
        }

        const currentSource = resolveSource();

        if (typeof currentSource === 'string')
        {
            dialog = document.querySelector<HTMLDialogElement>(`[id="${CSS.escape(currentSource)}"]`);
        }
        else if (currentSource instanceof HTMLDialogElement)
        {
            dialog = currentSource;
        }
        else
        {
            dialog = null;
        }

        if (model && dialog && dialog !== boundDialog)
        {
            boundDialog?.removeEventListener('close', onClose);
            boundDialog = dialog;
            boundDialog.addEventListener('close', onClose);
        }

        return dialog;
    }

    function onClose(event: Event)
    {
        if ((event.currentTarget as HTMLDialogElement).open)
            return;

        if (model)
        {
            model.value = false;
        }
    }

    if (model)
    {
        watch(() => model.value, async (value) =>
        {
            await nextTick();

            if (value !== model.value)
                return;

            if (value)
            {
                open();

                return;
            }

            close();
        });

        onMounted(() =>
        {
            resolveDialog();

            if (model.value)
            {
                open();
            }
        });

        onBeforeUnmount(() =>
        {
            boundDialog?.removeEventListener('close', onClose);
        });
    }

    async function open(guard?: (() => boolean) | Event)
    {
        const currentDialog = resolveDialog();
        const canOpen = typeof guard === 'function' ? guard : undefined;

        if (!currentDialog || currentDialog.open || !currentDialog.isConnected || (canOpen && !canOpen()))
            return;

        const request = {};

        pendingDialogOpens.set(currentDialog, request);
        currentDialog.returnValue = '';
        currentDialog.dispatchEvent(new CustomEvent('ideo-modal-open'));
        await nextTick();

        if (pendingDialogOpens.get(currentDialog) !== request)
            return;

        pendingDialogOpens.delete(currentDialog);

        if (!currentDialog.isConnected || resolveDialog() !== currentDialog || (canOpen && !canOpen()))
        {
            currentDialog.dispatchEvent(new CustomEvent('ideo-modal-open-cancelled'));

            if (model)
                model.value = false;

            return;
        }

        if (!currentDialog.open)
        {
            currentDialog.showModal();
            currentDialog.dispatchEvent(new CustomEvent('ideo-modal-opened'));
            registerModal(currentDialog);
        }

        if (model)
            model.value = true;
    }

    function close()
    {
        const currentDialog = resolveDialog();

        if (currentDialog && pendingDialogOpens.delete(currentDialog))
        {
            currentDialog.dispatchEvent(new CustomEvent('ideo-modal-open-cancelled'));

            if (model)
                model.value = false;
        }

        if (!currentDialog || !currentDialog.open)
        {
            return;
        }

        const request = new CustomEvent('ideo-modal-request-close', {
            cancelable: true,
            detail: {
                returnValue: ''
            }
        });

        if (currentDialog.dispatchEvent(request))
        {
            currentDialog.returnValue = '';
            currentDialog.close();
        }
    }

    return {
        get dialog()
        {
            return resolveDialog();
        },
        open,
        close
    };
}
